import React, { useState } from "react";
import * as IO from "fp-ts/IO";

import DomainExpertiseIcon from "src/assets/images/domain-expertise.png";
import DomainLearningIcon from "src/assets/images/domain-learning.png";
import DomainPurposeIcon from "src/assets/images/domain-purpose.png";
import DomainRelationshipsIcon from "src/assets/images/domain-relationships.png";
import DomainWellBeingIcon from "src/assets/images/domain-wellbeing.png";
import img from "src/assets/images/fsci_report_logo.png";
import { Button } from "src/components/button/button";
import { Modal } from "src/components/modal/modal";
import { Separator } from "src/components/separator/separator";
import { Body, PageTitle, SectionTitle } from "src/components/typography/typography";
import { School } from "src/generated/types";
import { FSCIBrandedV1Report } from "src/pages/reports/fsci/v1/types";

import { ExpertiseResources } from "./sections/expertise_resources/expertise_resources";
import { Purpose } from "./sections/purpose/purpose";
import { Relationships } from "./sections/relationships/relationships";
import { LearningOrientation } from "./sections/learning_orientation/learning_orientation";
import { MeaningfulStrengths } from "./sections/meaningful_strengths/meaningful_strengths";
import { KeyGrowthOpportunities } from "./sections/key_growth_opportunities/key_growth_opportunities";
import { WellBeing } from "./sections/well_being/well_being";

/*
 * Types
 */

interface ReportFullPageProps {
    readonly report: FSCIBrandedV1Report;
    readonly school: School;
}

/*
 * Main
 */

export const ReportFullPage = (props: ReportFullPageProps): React.ReactElement => {
    const { report, school } = props;

    const [isModalOpen, setModalOpen] = useState<boolean>(true);

    const convertToPdf: IO.IO<void> = () => window.print();

    return (
        <div className="main-page-wrap report full-report fsci-report">
            <div className="center">
                <div className="report-page-top">
                    <div className="report-page-title-line">
                        <img alt="ACSI Flourishing School Culture Instrument" className="fsci-report-image" src={img} />
                        <PageTitle>{school.name}</PageTitle>
                    </div>
                    <Separator />
                </div>

                <div className="prepended-page-wrap">
                    <div className="report-additional-page page-0">
                        <div className="center">
                            <img alt="ACSI FSCI" className="fsci-report-printable-image" src={img} />
                            <PageTitle className="fsci-report-title">
                                Flourishing School Culture Initiative
                                <br />
                                {school && <span>{`For ${school.name}`}</span>}
                            </PageTitle>
                        </div>
                    </div>
                    <div className="report-additional-page page-1">
                        <div className="center">
                            <SectionTitle>Introduction</SectionTitle>
                            <Body>
                                Thank you for your school’s participation in the Flourishing School Culture Instrument
                                (FSCI). ACSI is pleased to partner with committed schools like yours across the country
                                to collect and leverage empirical data in the pursuit of insight around how and why
                                Christian schools flourish.
                            </Body>
                            <Body>
                                The first administration of the Flourishing Schools Culture Instrument (FSCI) tested
                                1,445 discrete variables across seven different survey groups: students; parents;
                                alumni; teachers; leaders/administrators; support staff; and board members. Between Fall
                                2018 and Spring 2019, over 15,000 survey responses were collected from Christian schools
                                of diverse size and geographic location, thereby making the FSCI the largest study of
                                flourishing in Christian schools to date. In subsequent administrations since then, with
                                Christian schools in both the United States and around the world, we continue to build
                                upon the insights around flourishing in Christian schools to deliver validated and
                                relevant insights.
                            </Body>
                            <Body>
                                The validated constructs identified through FSCI data analysis were mapped onto the
                                first ever research-based model of Christian school flourishing—the Flourishing School
                                Culture Model (FSCM). [Detailed information regarding the methodology utilized to
                                generate the insights in this school-level report can be found in the FSCI national
                                report, <em>Flourishing Schools: Research on Christian School Culture and Community</em>
                                .] The FSCM clusters the validated constructs for all seven survey groups into five
                                domains of flourishing: Purpose; Relationships; Learning Orientation; Expertise and
                                Resources; and Well-Being. These domains provide a compelling and comprehensive picture
                                of the areas in which Christian schools can focus their efforts and resources in order
                                to promote a flourishing school culture and community.
                            </Body>
                            <Body>
                                All of your school’s data has been handled with the utmost care to ensure full
                                confidentiality. Your school’s profile, based on the results of the FSCI as mapped on to
                                the FSCM, is designed to highlight areas of existing strength as well as opportunities
                                for growth and improvement. Insights from the FSCI have the potential to inform
                                strategic planning and budgeting, professional development decisions, programmatic
                                priorities, marketing and promotional efforts, and much more.
                            </Body>
                            <Body>
                                As with most institutional survey research, it is important to note that FSCI findings
                                are best used when they are interpreted in light of school constituencies’ lived
                                experiences as collected through qualitative means. This means that schools will benefit
                                the most fully from this report when data is used to generate reflection, dialogue, and
                                collaborative planning for the present and the future. Please be sure to review the
                                report section entitled “Responding to Your School’s Results,” on the last two pages of
                                the report, for suggestions in this regard.
                            </Body>
                            <Body>
                                Thank you again for your school’s participation in the FSCI. We pray the insights in
                                this report will help your school to continue to grow in the five domains of Christian
                                school flourishing, and that ultimately—“May the Lord cause you to flourish, both you
                                and your children” (Psalm 115:14 NIV).
                            </Body>
                            <Body>
                                For questions regarding this report, please contact{" "}
                                <a href="mailto:assessmentsupport@acsi.org">assessmentsupport@acsi.org</a>.
                            </Body>
                        </div>
                    </div>
                    <div className="report-additional-page page-2">
                        <div className="center">
                            <SectionTitle>Overview of the Flourishing School Culture Model</SectionTitle>
                            <Body>
                                Analysis of the FSCI identified 35 validated constructs related to flourishing across
                                the seven groups of participants. These constructs have been grouped into a predictive
                                model with five major domains of flourishing in Christian schools:{" "}
                                <em>Purpose; Relationships; Learning Orientation; Expertise and Resources;</em> and{" "}
                                <em>Well-Being.</em> In the following pages, a definitional overview will be provided
                                for these domains, followed by a listing of specific constructs that make up each
                                domain.
                            </Body>
                            <div className="model-image-wrap report-page">
                                <img
                                    alt="Flourishing School Culture Model: Purpose, Relationships, Teaching and Learning, Expertise and Resources, and Well-Being support flourishing schools, educators, and students."
                                    src={img}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="report-additional-page page-3">
                        <div className="center">
                            <div className="report-split-content">
                                <div className="split-part-left">
                                    <Body>
                                        <strong className="blue">
                                            Defining <em>Purpose</em>
                                        </strong>
                                    </Body>
                                    <Body>
                                        A Christian perspective on education provides the <em>telos</em>, or purposeful
                                        aim, of Christian schooling. The FSCI identified a commitment on the part of all
                                        school constituencies to the central purposes of education as strongly connected
                                        with flourishing outcomes.
                                    </Body>
                                    <Body>
                                        Many of the distinctives of Christian education—meaning what is unique to
                                        Christian schools’ missions, values, and expected outcomes, as opposed to other
                                        sectors of education—fall within this domain. FSCI data analysis identified
                                        constructs related to the domain of Purpose for all seven survey groups
                                        (students; parents; alumni; teachers; leaders/administrators; support staff; and
                                        board members), below.
                                    </Body>
                                </div>
                                <div className="split-part-right">
                                    <img alt="purpose" src={DomainPurposeIcon} />
                                </div>
                                <div className="clear" />
                            </div>
                            <div className="construct-wrapper">
                                <SectionTitle>PURPOSE CONSTRUCTS</SectionTitle>
                                <Body>
                                    <strong>Responsibility</strong> – Leaders, teachers, and support staff feel a sense
                                    of shared ownership for school mission, success, and improvement.
                                </Body>
                                <Body>
                                    <strong>Holistic Teaching</strong> – Teaching involves helping students develop
                                    spiritually and emotionally (teaching the heart and soul, as well as the mind).
                                </Body>
                                <Body>
                                    <strong>Integrated Worldview</strong> – Christian worldview changes how we educate;
                                    there is no such thing as a secular sphere.
                                </Body>
                                <Body>
                                    <strong>God’s Story</strong> – Students believe they are a part of God’s bigger plan
                                    and can be used by him to “make a difference.”
                                </Body>
                                <Body>
                                    <strong>Questioning</strong> – Students have doubts about their faith, lack time to
                                    pray or study the Bible, and feel that most Christians are too judgmental.
                                </Body>
                                <Body>
                                    <strong>Partnership</strong> – Parents feel they are a part of the school’s mission,
                                    and that their child’s spiritual development requires their partnering with and
                                    being involved at the school.
                                </Body>
                                <Body>
                                    <strong>Spiritual Formation</strong> – Alumni report that their Christian faith is
                                    stronger thanks to attending a Christian school, and they believe people can change
                                    with God’s help.
                                </Body>
                            </div>
                        </div>
                    </div>
                    <div className="report-additional-page page-4">
                        <div className="center">
                            <div className="report-split-content">
                                <div className="split-part-left">
                                    <Body>
                                        <strong className="blue">
                                            Defining <em>Relationships</em>
                                        </strong>
                                    </Body>
                                    <Body>
                                        Education is a relational enterprise by nature. For Christian schools in
                                        particular, relationships are important because of the incarnational nature of
                                        Christian faith, expressed through community and in discipleship (John 1:14, 1
                                        Corinthians 12:12-27, Ephesians 4:16).
                                    </Body>
                                    <Body>
                                        The FSCI identified trust-filled, supportive, and authentic relationships
                                        between all school constituencies, as well as with the surrounding community, as
                                        key to flourishing outcomes. The constructs related to Relationship were
                                        identified for all seven survey groups (students; parents; alumni; teachers;
                                        leaders/administrators; support staff; and board members), below.
                                    </Body>
                                </div>
                                <div className="split-part-right">
                                    <img alt="purpose" src={DomainRelationshipsIcon} />
                                </div>
                                <div className="clear" />
                            </div>
                            <div className="construct-wrapper">
                                <SectionTitle>RELATIONSHIP CONSTRUCTS</SectionTitle>
                                <Body>
                                    <strong>Supportive Leadership</strong> – Principals are trusted, teachers feel that
                                    leaders “have our backs,” and leaders empower teachers and staff to make decisions.
                                </Body>
                                <Body>
                                    <strong>Leadership Interdependence</strong> – Diverse backgrounds, transparency
                                    about one’s weaknesses, and relying on others to offset those weaknesses is key.
                                </Body>
                                <Body>
                                    <strong>Parent Relationships</strong> – Teachers “get to know” parents, and frequent
                                    and systemic communication facilitates positive relationships.
                                </Body>
                                <Body>
                                    <strong>Community Engagement</strong> – The school engages with the surrounding
                                    community and regularly taps into community resources, including networking and
                                    resource-sharing with other schools.
                                </Body>
                                <Body>
                                    <strong>Mentoring Students</strong> – Staff point out talent in each student, help
                                    students see how they fit in God’s bigger plan, and are aware of students’ struggles
                                    at school or home.
                                </Body>
                                <Body>
                                    <strong>Insular Culture</strong> – The school shields students from the world’s
                                    brokenness, the school is independent from the surrounding community, and/or the
                                    student body lacks diversity.
                                </Body>
                                <Body>
                                    <strong>Christlike Teachers</strong> – Teachers show Christlike love, kindness, and
                                    care to students. Students are cared about individually, including their spiritual
                                    development.
                                </Body>
                                <Body>
                                    <strong>Prosocial Orientation</strong> – Students not only enjoy helping others, but
                                    also are known by others (e.g., peers) for showing love and care.
                                </Body>
                                <Body>
                                    <strong>Caring Environment</strong> – From the perspective of school graduates,
                                    teachers were kind, students felt included in class, and students were protected
                                    from bullying.
                                </Body>
                            </div>
                        </div>
                    </div>
                    <div className="report-additional-page page-5">
                        <div className="center">
                            <div className="report-split-content">
                                <div className="split-part-left">
                                    <Body>
                                        <strong className="blue">
                                            Defining <em>Learning Orientation</em>
                                        </strong>
                                    </Body>
                                    <Body>
                                        Schools are of course known as sites of learning for students. But if school
                                        cultures are to flourish, schools and educators themselves must also engage in
                                        learning. In fact, a school culture in which educators are committed to ongoing
                                        learning and improvement is linked to flourishing not only for the school and
                                        educators, but also for students.
                                    </Body>
                                    <Body>
                                        FSCI data analysis identified constructs related to{" "}
                                        <em>Learning Orientation</em> for five of the seven survey groups (alumni;
                                        teachers; leaders/administrators; support staff; and board members), as follows.
                                    </Body>
                                </div>
                                <div className="split-part-right">
                                    <img alt="purpose" src={DomainLearningIcon} />
                                </div>
                                <div className="clear" />
                            </div>
                            <div className="construct-wrapper">
                                <SectionTitle>LEARNING ORIENTATION CONSTRUCTS</SectionTitle>
                                <Body>
                                    <strong>Feedback</strong> – Feedback on teaching practice and classroom management
                                    is given regularly to facilitate adjustments in real-time.
                                </Body>
                                <Body>
                                    <strong>Collaboration</strong> – Learning from and with other teachers drives and
                                    inspires better teaching.
                                </Body>
                                <Body>
                                    <strong>Systems Thinking</strong> – When planning for change, the potential impact
                                    on the school, the classroom, students, and the overall system are considered.
                                </Body>
                                <Body>
                                    <strong>Data-Driven Improvement</strong> – Data is used to gauge school results and
                                    effectiveness, determine goal attainment, and address problems the school faces.
                                </Body>
                                <Body>
                                    <strong>Professional Development</strong> – PD is provided on-site and is subject-
                                    and role-specific.
                                </Body>
                                <Body>
                                    <strong>Outcomes Focus</strong> – A strong belief is held that process doesn’t
                                    matter if it isn’t producing results, and change is distracting if it doesn’t lead
                                    to increases in student achievement.
                                </Body>
                                <Body>
                                    <strong>Culture of Improvement</strong> – Guided by school leadership and focused on
                                    the future, the school is continually improving/makes necessary changes to improve.
                                </Body>
                                <Body>
                                    <strong>Individualized Instruction</strong> – Students are helped to figure out how
                                    they learn best and to identify their natural strengths.
                                </Body>
                            </div>
                        </div>
                    </div>
                    <div className="report-additional-page page-6">
                        <div className="center">
                            <div className="report-split-content">
                                <div className="split-part-left">
                                    <Body>
                                        <strong className="blue">
                                            Defining <em>Expertise &amp; Resources</em>
                                        </strong>
                                    </Body>
                                    <Body>
                                        While a sense of purpose is essential for Christian school leaders and teachers,
                                        their ability to fulfill that purpose is necessarily dependent upon their
                                        expertise as educators. FCSI research shows that flourishing is connected to
                                        excellence in educational and school management practices.
                                    </Body>
                                    <Body>
                                        Constructs related to expertise and resources were significant for five of the
                                        seven participant groups surveyed by the FSCI (parents; teachers;
                                        leaders/administrators; support staff; and board members).
                                    </Body>
                                </div>
                                <div className="split-part-right">
                                    <img alt="purpose" src={DomainExpertiseIcon} />
                                </div>
                                <div className="clear" />
                            </div>
                            <div className="construct-wrapper">
                                <SectionTitle>EXPERTISE &amp; RESOURCES CONSTRUCTS</SectionTitle>
                                <Body>
                                    <strong>Best Practice Orientation</strong> – Keeping up with best practices in
                                    teaching is prioritized and resources for doing so can be identified.
                                </Body>
                                <Body>
                                    <strong>Engaged Learning</strong> – Students engage in activities that nurture
                                    critical thinking, evaluating information, and problem solving.
                                </Body>
                                <Body>
                                    <strong>Classroom Management</strong> – The classroom is orderly, and teachers are
                                    organized and consistent with discipline.
                                </Body>
                                <Body>
                                    <strong>Responsiveness to Special Needs</strong> – Teaching staff works together to
                                    serve students with special needs, aided by processes for identifying and responding
                                    to those needs.
                                </Body>
                                <Body>
                                    <strong>Qualified Staff</strong> – New teacher hires are credentialed (educationally
                                    and licensed/certified) and have classroom experience.
                                </Body>
                                <Body>
                                    <strong>Resources</strong> – Materials and resources for teaching, including
                                    technology, are sufficient, and the school building is in good physical condition.
                                </Body>
                                <Body>
                                    <strong>Resource Planning</strong> – A strategic financial plan and master
                                    facilities plan is in place, and financial planning is a strength of the board.
                                </Body>
                                <Body>
                                    <strong>Resource Constraints</strong> – The school has financial resources to
                                    operate effectively; or a belief is held that the school could be more effective if
                                    not for fiscal constraints, and the school lacks the resources needed to make
                                    changes in the school.
                                </Body>
                            </div>
                        </div>
                    </div>
                    <div className="report-additional-page page-7">
                        <div className="center">
                            <div className="report-split-content">
                                <div className="split-part-left">
                                    <Body>
                                        <strong className="blue">
                                            Defining <em>Well-Being</em>
                                        </strong>
                                    </Body>
                                    <Body>
                                        Well-being is an important domain for both student and educator flourishing. The
                                        FSCI identified three constructs related to well-being—one for teachers and
                                        leaders (Stress), and two for students (Healthy Living and Resilience). This
                                        domain and related constructs demonstrate that the well-being of educators and
                                        students is not a secondary concern—but an essential contributor to flourishing
                                        outcomes.
                                    </Body>
                                </div>
                                <div className="split-part-right">
                                    <img alt="purpose" src={DomainWellBeingIcon} />
                                </div>
                                <div className="clear" />
                            </div>
                            <div className="construct-wrapper">
                                <SectionTitle>WELL-BEING CONSTRUCTS</SectionTitle>
                                <Body>
                                    <strong>Stress</strong> – Constant feelings of stress and being overwhelmed
                                    accompany a lack of time to prepare for instruction (for teachers), or to focus on
                                    physical health (for leaders).
                                </Body>
                                <Body>
                                    <strong>Healthy Living</strong> – Students are happy with their physical health,
                                    including sufficient exercise and a healthy diet.
                                </Body>
                                <Body>
                                    <strong>Resilience</strong> – Students handle stress effectively and respond well
                                    to/bounce back from difficult situations.
                                </Body>
                            </div>
                        </div>
                    </div>
                </div>

                <MeaningfulStrengths report={report} />
                <KeyGrowthOpportunities report={report} />
                <Purpose report={report} />
                <Relationships report={report} />
                <LearningOrientation report={report} />
                <ExpertiseResources report={report} />
                <WellBeing report={report} />

                <div className="prepended-page-wrap">
                    <div className="report-additional-page page-8">
                        <div className="center">
                            <SectionTitle>Responding to Your School’s Results</SectionTitle>
                            <Body>
                                <strong>Responding to Meaningful Strengths</strong>
                            </Body>
                            <Body>
                                This report shared five strengths of your school identified by the FSCI in regard to
                                creating and sustaining a flourishing school culture. These areas emerged from your
                                school’s overall FSCI profile (meaning these areas are <em>not</em> benchmarked against
                                other Christian schools, but are the top five strengths that emerged from your school’s
                                data and are specific to your school’s unique profile). These strengths are unique to
                                your school and, as such, represent areas to affirm, share, and celebrate with your
                                school community, as well as external audiences. Some suggestions for how to do this
                                are:
                            </Body>
                            <ul>
                                <li>
                                    <strong>
                                        <em>Share your school’s strengths.</em>
                                    </strong>{" "}
                                    School families will want to know what makes their children’s school unique.
                                    Consider communicating these research-based findings through school correspondence,
                                    on social media, and at school gatherings.
                                </li>
                                <li>
                                    <strong>
                                        <em>Celebrate internally.</em>
                                    </strong>{" "}
                                    Provide positive feedback to school constituents who contribute directly or
                                    indirectly to making these areas key strengths of your school.
                                </li>
                                <li>
                                    <strong>
                                        <em>Communicate externally.</em>
                                    </strong>{" "}
                                    External stakeholders should know your school cares about flourishing, and the FSCI
                                    provides quantitative validation of your school’s success in these areas. Along
                                    these lines, determine which of these strengths could be shared in marketing
                                    to/recruitment of prospective students and families.
                                </li>
                                <li>
                                    <strong>
                                        <em>Build on momentum.</em>
                                    </strong>{" "}
                                    Discuss with relevant internal stakeholders (board, leaders, staff) ways in which
                                    areas of strength can be reinforced and built out further.
                                </li>
                                <li>
                                    <strong>
                                        <em>Go deeper.</em>
                                    </strong>{" "}
                                    Study whether any quantifiable outcomes that your school tracks can be tied to the
                                    strengths identified by the FSCI, and/or conduct qualitative interviews or focus
                                    groups with stakeholders around these strengths. This will give you a greater
                                    understanding of why and how these are areas of strength for your school.
                                </li>
                                <li>
                                    <strong>
                                        <em>Track growth.</em>
                                    </strong>{" "}
                                    Keep track of any changes, programs, and initiatives, so you can understand what
                                    might be driving any shifts in future years’ results.
                                </li>
                            </ul>
                            <Body className="more-top">
                                <strong>Responding to Key Growth Opportunities</strong>
                            </Body>
                            <Body>
                                This report shared five areas of your school that represent key growth opportunities
                                identified by the FSCI, in regard to creating and sustaining a flourishing school
                                culture (again, meaning these areas are <em>not</em> benchmarked against other Christian
                                schools, but emerged from your school’s data and are specific to your school’s unique
                                profile). These areas can serve as potential focus points for long-range planning and
                                school improvement efforts. Some suggestions for how to do this are:
                            </Body>
                            <ul>
                                <li>
                                    <strong>
                                        <em>Reflect together.</em>
                                    </strong>{" "}
                                    Every school has its own areas where focused efforts will deliver a meaningful
                                    return, and the FSCI helps to identify those areas. Instead of thinking of these
                                    areas as “problems” to solve, gather a group of various stakeholders and discuss
                                    what it might look like to grow constructively in these areas.
                                </li>
                                <li>
                                    <strong>
                                        <em>Go deeper.</em>
                                    </strong>{" "}
                                    Study whether any quantifiable outcomes that your school tracks can be tied to areas
                                    for growth identified by the FSCI, and/or conduct qualitative interviews or focus
                                    groups with stakeholders around these areas. This will give you a greater
                                    understanding of why these are areas for growth in your school, as well as how to
                                    address them successfully.
                                </li>
                                <li>
                                    <strong>
                                        <em>Use insights in existing efforts.</em>
                                    </strong>{" "}
                                    Whether in strategic planning, school improvement planning, or accreditation
                                    efforts, the growth opportunities identified by the FSCI can help inform your
                                    ongoing efforts to shape and strengthen your school culture.
                                </li>
                                <li>
                                    <strong>
                                        <em>Develop new targeted efforts.</em>
                                    </strong>{" "}
                                    Consider developing a new program or initiative to address a key growth opportunity
                                    identified by the FSCI. Start by researching best practice in this area and/or
                                    visiting other schools with demonstrated success in this area. Be sure to include
                                    input from multiple stakeholders in the development process for new programs and
                                    initiatives.
                                </li>
                                <li>
                                    <strong>
                                        <em>Track growth.</em>
                                    </strong>{" "}
                                    Keep track of any changes, programs, and initiatives, so you can understand what
                                    might be driving any shifts in future years’ results.
                                </li>
                            </ul>
                            <Body className="more-top">
                                <strong>Responding to Other Profile Results</strong>
                            </Body>
                            <Body>
                                FSCI research demonstrates that school flourishing is not “one-size-fits-all.” Every
                                school has a different culture profile, along with potentially different ways of
                                arriving at the same flourishing outcomes. Thus, the benchmark results based on all
                                participating schools are not included for the purpose of scoring or ranking your school
                                against other schools. Rather, they are provided to your school for two key reasons:
                            </Body>
                            <ol>
                                <li>
                                    <strong>
                                        <em>To help your school gauge priorities</em>
                                    </strong>
                                    , by providing your school with data about areas of relative strength and areas to
                                    target for growth. It could be said that the FSCI takes the cultural “temperature”
                                    of your school in these areas. To learn more, and as mentioned above, consider
                                    “going deeper” for constructs that relate to school priorities (i.e. by studying
                                    related quantifiable outcomes that your school tracks and/or conducting qualitative
                                    interviews or focus groups with stakeholders).
                                </li>
                                <li>
                                    <strong>
                                        <em>To provide benchmark data for each construct</em>
                                    </strong>
                                    , so that you can track any changes over time as measured by repeat assessment with
                                    the FSCI. In order to do this—as suggested above—keep track of any changes,
                                    programs, and initiatives, so you can understand what might be driving any shifts in
                                    future years’ results.
                                </li>
                            </ol>
                            <Body>
                                Finally, while reviewing your school’s profile results,{" "}
                                <strong>
                                    <em>consult the FSCI national report on an annual basis</em>
                                </strong>{" "}
                                for linkages identified between constructs and flourishing outcomes. If your school’s
                                areas of strength and/or growth opportunities identified by the FSCI match the outcome
                                linkages highlighted in the national report, you may wish to prioritize those in terms
                                of investing energy and resources, given the predictive nature of those linkages.
                            </Body>
                            <Body className="more-top">
                                <strong>For More Information</strong>
                            </Body>
                            <Body className="last-paragraph">
                                Specific questions regarding the FSCI and/or your school’s results can be directed to{" "}
                                <a href="mailto:assessmentsupport@acsi.org">assessmentsupport@acsi.org</a>.
                            </Body>
                        </div>
                    </div>
                </div>
            </div>

            <Modal isOpen={isModalOpen} setOpen={setModalOpen} title="Print instructions">
                <Body>
                    You can either print a copy of your report or save it as a PDF. To save it as a PDF, click print,
                    expand the printer dropdown, and choose "Save as PDF."
                </Body>
                <Button
                    onClick={() => {
                        setModalOpen(false);
                        setTimeout(() => convertToPdf(), 1000);
                    }}
                >
                    Ok
                </Button>
            </Modal>
        </div>
    );
};
